import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import Loader from "../../../Layouts/Loader/Loader";
import { useStatemachineContext } from "../../../context/StatemachineContext";
import { processAiSmConfigForFormFields } from "../../../commonFunctions/formFunc";
import { generateObjectId } from "../../../utils/objectIdUtils";
import { AI_PROVIDER_LIST } from "../../../constant/constant";

const SmAiChatContainer = ({ onRecieve }) => {
  const { orgId, smId } = useParams();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);
  const [chatId, setChatId] = useState("");
  const { setSelectedStateMachine } = useStatemachineContext();
  const [selectedAiModel, setSelectedAiModel] = useState("gemini");
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState("");

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleFormClick = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleChange = (e) => {
    setPrompt(e.target.value);
    setError(null);
  };

  const handleAiModelChange = (e) => {
    e.stopPropagation();
    setSelectedAiModel(e.target.value);
  };

  const submitPrompt = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    setLoading(true);
    setError(null);

    const newChatHistory = [...chatHistory, { id: generateObjectId(), sender: "user", message: prompt }];
    setChatHistory(newChatHistory);

    try {
      const payload = {
        org_id: orgId,
        sm_id: smId,
        ai_model: selectedAiModel,
        prompt,
        chat_id: chatId
      };
      const { data } = await axiosInstance.post("/ai/UpdateStateMachineAI", payload, {
        timeout: 90000
      });
      setChatId(data.chat_id);
      const newAiChatId = generateObjectId();
      setChatHistory([
        ...newChatHistory,
        { id: newAiChatId, sender: "ai", message: data.ai_response || "AI response here...", data: data }
      ]);
      setSelectedChatId(newAiChatId);
      setPrompt("");
      sendSmConfig(data);
    } catch (error) {
      setError("Something went wrong. Please try again.");
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const sendSmConfig = (data) => {
    const newSmConfig = processAiSmConfigForFormFields(data.sm_config);
    onRecieve(newSmConfig);
  };

  const onClickOnAiChat = (id) => {
    const selectedChat = chatHistory.find((chatHistory) => chatHistory.id === id);
    if (selectedChat) {
      setSelectedChatId(selectedChat.id);
      sendSmConfig(selectedChat.data);
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [prompt]);

  useEffect(() => {
    setSelectedStateMachine({
      id: smId,
      name: "",
      stateid: smId,
      statename: "statemachine"
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sm_ai_chat_container">
      <div
        style={{ flex: 1, padding: "10px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px" }}
      >
        {chatHistory.length === 0 && <div style={{ textAlign: "center", color: "#999" }}>Start a conversation...</div>}
        {chatHistory.map((chat, index) => (
          <div
            key={index}
            style={{
              alignSelf: chat.sender === "user" ? "flex-end" : "flex-start",
              background: chat.sender === "user" ? "#e0e0e0" : selectedChatId === chat.id ? "lightblue" : "transparent",
              color: chat.sender === "user" ? "#000" : "#000",
              padding: "6px 12px",
              borderRadius: "6px",
              maxWidth: "75%",
              wordBreak: "break-word",
              cursor: chat.sender === "ai" ? "pointer" : "default"
            }}
            onClick={() => chat.sender === "ai" && onClickOnAiChat(chat.id) && null}
          >
            {chat.message}
          </div>
        ))}
      </div>

      {/* Chat Input Form */}
      <form className="sm_ai_prompt_form" onSubmit={submitPrompt} onClick={handleFormClick}>
        <textarea
          ref={textareaRef}
          className="sm_ai_input_box w_100 border_none p_0"
          placeholder="Ask Anything"
          value={prompt}
          onChange={handleChange}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && submitPrompt(e)}
          rows={1}
          style={{
            resize: "none",
            overflow: "hidden",
            minHeight: "20px",
            maxHeight: "200px"
          }}
          disabled={loading}
        />
        <div className="d_flex justify_end align_center">
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <select
            className="ai_model_selector"
            value={selectedAiModel}
            onChange={handleAiModelChange}
            disabled={loading}
            onClick={(e) => e.stopPropagation()}
          >
            {AI_PROVIDER_LIST.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className={`material-symbols-outlined sm_ai_button_icon primary ${loading ? "disabled" : ""}`}
            disabled={loading}
          >
            {loading ? <Loader width={20} /> : "send"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SmAiChatContainer;
